import { ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import type { TablePaginationConfig } from "antd";
import {
	Alert,
	Badge,
	Button,
	Card,
	Col,
	Row,
	Select,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Typography,
	Upload,
	message,
	notification,
} from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { authAxios, getEmailFromSessionStorage } from "../utils/session_utils";

const { Title, Text } = Typography;

const PROGRAM_OPTIONS = [
	{ value: "tegna", label: "Bundle - Tegna" },
	{ value: "american spirit", label: "Bundle - American Spirit" },
	{ value: "hearst", label: "Bundle - Hearst" },
	{ value: "gray", label: "Bundle - Gray" },
	{ value: "univision", label: "Bundle - Univision" },
	{ value: "paid_programming", label: "Paid Programming" },
];

const PROGRAM_LABELS: Record<string, string> = PROGRAM_OPTIONS.reduce(
	(acc, option) => ({ ...acc, [option.value]: option.label }),
	{},
);

// The backend sends status_type precisely so the UI does not have to guess.
// This map only turns that hint into an AntD colour.
const STATUS_TAG_COLOR: Record<string, string> = {
	default: "default",
	processing: "processing",
	success: "success",
	error: "error",
};

// A job in one of these will never change again, so polling stops once every
// visible row is terminal.
const TERMINAL_STATUSES = ["COMPLETED", "FAILED"];

const POLL_INTERVAL_MS = 5000;

const UPLOAD_TAB = "upload";
const JOBS_TAB = "jobs";

export interface BundlePostLogJob {
	job_id: string;
	program: string | null;
	file_name: string | null;
	status: string;
	status_type: string;
	created_by: string | null;
	created_at: string | null;
	updated_at: string | null;
}

const formatDateTime = (value: string | null): string => {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const BundlePostLogUploader = () => {
	const [fileList, setFileList] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [program, setProgram] = useState<string>("");
	const [activeTab, setActiveTab] = useState<string>(UPLOAD_TAB);

	const [jobs, setJobs] = useState<BundlePostLogJob[]>([]);
	const [jobsLoading, setJobsLoading] = useState<boolean>(false);
	const [jobsError, setJobsError] = useState<string | null>(null);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		total: 0,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50"],
		showTotal: (total: number) => `${total} job${total === 1 ? "" : "s"}`,
	});

	// Read inside the polling effect without making it a dependency, so changing
	// pages does not tear down and recreate the timer.
	const paginationRef = useRef(pagination);
	useEffect(() => {
		paginationRef.current = pagination;
	}, [pagination]);

	// Monotonic request token: a response is only applied if it belongs to the
	// most recent request, so a slow poll can never clobber a newer page.
	const jobsRequestRef = useRef(0);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const fetchJobs = useCallback(
		async (overrides?: { current?: number; pageSize?: number }, options?: { silent?: boolean }) => {
			const current = overrides?.current ?? paginationRef.current.current ?? 1;
			const pageSize = overrides?.pageSize ?? paginationRef.current.pageSize ?? 10;

			const requestId = ++jobsRequestRef.current;
			const isCurrent = () => mountedRef.current && jobsRequestRef.current === requestId;

			if (!options?.silent) setJobsLoading(true);

			try {
				const response = await authAxios({
					method: "GET",
					url: `${process.env.REACT_APP_BACK_END_API}/salesforce/bundle-post-log/jobs`,
					params: { pagination: JSON.stringify({ current, pageSize }) },
				});

				if (!response?.data?.success) {
					throw new Error(response?.data?.message || "Failed to load jobs.");
				}

				if (!isCurrent()) return;

				setJobs(response.data.jobs ?? []);
				setPagination(prev => ({
					...prev,
					current,
					pageSize,
					total: response.data.total ?? 0,
				}));
				setJobsError(null);
			} catch (error: any) {
				console.error("Failed to load bundle post log jobs:", error);
				if (!isCurrent()) return;
				const detail =
					error?.response?.data?.message || error?.message || "Unable to reach the server.";
				// A failed background poll should not wipe the table the user is reading.
				if (!options?.silent) {
					setJobsError(detail);
				}
			} finally {
				if (!options?.silent && isCurrent()) setJobsLoading(false);
			}
		},
		[],
	);

	useEffect(() => {
		fetchJobs({ current: 1 });
	}, [fetchJobs]);

	// Poll only while something can still change. Refresh stays available either way.
	const activeJobCount = jobs.filter(job => !TERMINAL_STATUSES.includes(job.status)).length;
	const hasActiveJobs = activeJobCount > 0;

	useEffect(() => {
		if (!hasActiveJobs) return undefined;

		const timer = setInterval(() => {
			fetchJobs(undefined, { silent: true });
		}, POLL_INTERVAL_MS);

		return () => clearInterval(timer);
	}, [hasActiveJobs, fetchJobs]);

	const handleTableChange = (nextPagination: TablePaginationConfig) => {
		fetchJobs({ current: nextPagination.current, pageSize: nextPagination.pageSize });
	};

	const handleBeforeUpload = (file: File) => {
		const fileName = file.name.toLowerCase();
		const isCSV = file.type === "text/csv" || fileName.endsWith(".csv");

		const isExcel =
			file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
			file.type === "application/vnd.ms-excel" ||
			fileName.endsWith(".xlsx") ||
			fileName.endsWith(".xls");

		if (!isCSV && !isExcel) {
			message.error("Only CSV or Excel (.xlsx, .xls) files are allowed!");
			return Upload.LIST_IGNORE;
		}

		setFileList([
			{
				originFileObj: file,
				name: file.name,
				status: "done",
			},
		]);

		return false;
	};

	const handleOnChange = (info: any) => {
		setFileList(info.fileList);
	};

	const handleUpload = async () => {
		if (!program) {
			message.error("Please select a program.");
			return;
		}

		if (!fileList.length) {
			message.error("No file selected");
			return;
		}

		const selectedProgram = program;
		const selectedFileName = fileList[0].name;

		setLoading(true);
		const hideLoading = message.loading("Uploading file...", 0);

		try {
			const formData = new FormData();
			formData.append("file", fileList[0].originFileObj);
			formData.append("program", selectedProgram);
			formData.append("email", getEmailFromSessionStorage() || "");

			const response = await authAxios({
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/salesforce/bundle-post-log-uploader`,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" },
			});

			if (!response?.data?.success) {
				throw new Error(response?.data?.message || "Failed to upload file.");
			}

			const runId: string | undefined = response.data.runId;

			notification.success({
				message: "File queued for processing",
				description: runId
					? `Job ${runId} has been created. Track its progress in the Job Monitor tab.`
					: "Your file has been queued. Track its progress in the Job Monitor tab.",
				duration: 6,
			});

			// Show the job immediately rather than waiting for the refetch below.
			if (runId) {
				setJobs(prev => [
					{
						job_id: runId,
						program: selectedProgram,
						file_name: selectedFileName,
						status: "QUEUED",
						status_type: "default",
						created_by: getEmailFromSessionStorage() || null,
						created_at: new Date().toISOString(),
						updated_at: new Date().toISOString(),
					},
					...prev,
				]);
			}

			// Uploader is immediately free again — another file can go now.
			setFileList([]);
			setProgram("");

			// Jump back to page 1 where the new job lives, and show it.
			setActiveTab(JOBS_TAB);
			fetchJobs({ current: 1 });
		} catch (error: any) {
			console.error("Upload error:", error);
			const detail =
				error?.response?.data?.message || error?.message || "Unknown error";
			message.error(`Upload failed: ${detail}`);
		} finally {
			setLoading(false);
			hideLoading();
		}
	};

	const columns = [
		{
			title: "Job ID",
			dataIndex: "job_id",
			key: "job_id",
			width: 150,
			render: (value: string) => (
				<Tooltip title={value}>
					<Text code copyable={{ text: value }}>
						{value ? `${value.slice(0, 8)}…` : "—"}
					</Text>
				</Tooltip>
			),
		},
		{
			title: "Program",
			dataIndex: "program",
			key: "program",
			render: (value: string | null) => (value ? PROGRAM_LABELS[value] ?? value : "—"),
		},
		{
			title: "File Name",
			dataIndex: "file_name",
			key: "file_name",
			ellipsis: true,
			render: (value: string | null) =>
				value ? <Tooltip title={value}>{value}</Tooltip> : "—",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 140,
			render: (status: string, record: BundlePostLogJob) => (
				<Tag color={STATUS_TAG_COLOR[record.status_type] ?? "default"}>{status}</Tag>
			),
		},
		{
			title: "Created By",
			dataIndex: "created_by",
			key: "created_by",
			ellipsis: true,
			render: (value: string | null) =>
				value ? <Tooltip title={value}>{value}</Tooltip> : "—",
		},
		{
			title: "Created At",
			dataIndex: "created_at",
			key: "created_at",
			width: 190,
			render: formatDateTime,
		},
		{
			title: "Updated At",
			dataIndex: "updated_at",
			key: "updated_at",
			width: 190,
			render: formatDateTime,
		},
	];

	const uploadTab = (
		<>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Select
						placeholder="Select Program"
						size="large"
						style={{ width: "100%" }}
						value={program || undefined}
						onChange={value => setProgram(value)}
						options={PROGRAM_OPTIONS}
					/>
				</Col>
			</Row>
			<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
				<Col span={24}>
					<Upload
						beforeUpload={handleBeforeUpload}
						onChange={handleOnChange}
						fileList={fileList}
						maxCount={1}
						onRemove={() => setFileList([])}
					>
						<Button icon={<UploadOutlined />} disabled={loading} size="large">
							Select File
						</Button>
					</Upload>
				</Col>
			</Row>
			{fileList.length > 0 && (
				<Row style={{ marginTop: 16 }}>
					<Col span={24}>
						<Alert
							message="File Selected"
							description={`File "${fileList[0].name}" is ready to upload.`}
							type="info"
							showIcon
						/>
					</Col>
				</Row>
			)}

			<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
				<Col span={24}>
					<Button
						type="primary"
						onClick={handleUpload}
						disabled={!fileList.length || loading}
						loading={loading}
						size="large"
						block
					>
						{loading ? "Uploading..." : "Upload"}
					</Button>
				</Col>
			</Row>
		</>
	);

	const jobsTab = (
		<>
			{jobsError && (
				<Alert
					message="Could not load jobs"
					description={jobsError}
					type="error"
					showIcon
					closable
					onClose={() => setJobsError(null)}
					action={
						<Button size="small" onClick={() => fetchJobs()}>
							Retry
						</Button>
					}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Table
				rowKey="job_id"
				columns={columns}
				dataSource={jobs}
				loading={jobsLoading}
				pagination={pagination}
				onChange={handleTableChange}
				size="middle"
				scroll={{ x: 1000 }}
				locale={{ emptyText: "No upload jobs yet." }}
			/>
		</>
	);

	return (
		<Card
			title={<Title level={4} style={{ margin: 0 }}>Post Log Uploader</Title>}
			style={{ maxWidth: 1200, margin: "20px auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
			bordered={true}
		>
			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				// Only meaningful on the Job Monitor tab, so it is hidden elsewhere.
				tabBarExtraContent={
					activeTab === JOBS_TAB ? (
						<Button
							icon={<ReloadOutlined />}
							onClick={() => fetchJobs()}
							loading={jobsLoading}
						>
							Refresh
						</Button>
					) : null
				}
				items={[
					{ key: UPLOAD_TAB, label: "Upload File", children: uploadTab },
					{
						key: JOBS_TAB,
						label: (
							<Badge count={activeJobCount} size="small" offset={[8, -2]}>
								<span>Job Monitor</span>
							</Badge>
						),
						children: jobsTab,
					},
				]}
			/>
		</Card>
	);
};

export default BundlePostLogUploader;
